import { BigNumber } from '@ethersproject/bignumber'
import { useWeb3React } from '@web3-react/core'
import { CallStateResult, useSingleCallResult, useSingleContractMultipleData } from 'lib/hooks/multicall'
import { useMemo } from 'react'

import { V3_STAKER_ADDRESSES } from '../constants/addresses'
import { SupportedChainId } from '../constants/chains'
import { PositionDetails } from '../types/position'
import { areEqualAddresses } from '../utils/areEqualAddresses'
import { useV3NFTPositionManagerContract, useV3StakerContract } from './useContract'

// Extended position interface that includes staking information
interface StakedPositionDetails extends PositionDetails {
  stakingInfo: {
    owner: string
    numberOfStakes: number
    liquidity?: BigNumber
    secondsPerLiquidityInsideInitialX128?: BigNumber
  }
}

interface UseV3StakerPositionsFromUserResults {
  loading: boolean
  positions: StakedPositionDetails[] | undefined
}

/**
 * Returns positions that are held by the V3 staker contract and were deposited by the current user
 * @returns Object containing loading state and positions
 */
export function useV3StakerPositionsFromUser(): UseV3StakerPositionsFromUserResults {
  const { account, chainId } = useWeb3React()
  const positionManager = useV3NFTPositionManagerContract()
  const v3StakerContract = useV3StakerContract()
  const stakerAddress = useMemo(
    () => (chainId ? V3_STAKER_ADDRESSES[chainId as SupportedChainId] : undefined),
    [chainId]
  )

  // Get the staker's balance of NFTs
  const { loading: balanceLoading, result: balanceResult } = useSingleCallResult(positionManager, 'balanceOf', [
    stakerAddress ?? undefined,
  ])

  // Convert the result to a number (we don't expect any account balance to ever exceed the bounds of max safe int)
  const accountBalance: number | undefined = balanceResult?.[0]?.toNumber()

  // Create tokenOfOwnerByIndex requests for each position
  const tokenIdsArgs = useMemo(() => {
    if (accountBalance && stakerAddress) {
      const tokenRequests = []
      for (let i = 0; i < accountBalance; i++) {
        tokenRequests.push([stakerAddress, i])
      }
      return tokenRequests
    }
    return []
  }, [stakerAddress, accountBalance])

  // Fetch the token IDs for each index
  const tokenIdResults = useSingleContractMultipleData(positionManager, 'tokenOfOwnerByIndex', tokenIdsArgs)

  const someTokenIdsLoading = useMemo(() => tokenIdResults.some(({ loading }) => loading), [tokenIdResults])

  // Extract the token IDs from the results
  const tokenIds = useMemo(() => {
    if (stakerAddress) {
      return tokenIdResults
        .map(({ result }) => result)
        .filter((result): result is CallStateResult => !!result)
        .map((result) => BigNumber.from(result[0]))
    }
    return []
  }, [stakerAddress, tokenIdResults])

  // Get deposit information for each token ID
  const depositsArgs = useMemo(() => {
    if (tokenIds.length) {
      return tokenIds.map((tokenId) => [tokenId.toString()])
    }
    return []
  }, [tokenIds])

  const depositResults = useSingleContractMultipleData(v3StakerContract, 'deposits', depositsArgs)
  const depositsLoading = useMemo(() => depositResults.some(({ loading }) => loading), [depositResults])

  // Parse deposit info and filter for the current user's deposits
  const userDeposits = useMemo(() => {
    if (!account) return { depositInfos: [], userTokenIds: [] }

    const depositInfos = depositResults
      .map(({ result }, index) => {
        if (!result) return null
        const owner = result.owner
        const numberOfStakes = Number(result.numberOfStakes || 0)
        const tokenId = tokenIds[index]
        return { owner, numberOfStakes, tokenId }
      })
      .filter(
        (deposit): deposit is { owner: string; numberOfStakes: number; tokenId: BigNumber } =>
          deposit !== null && areEqualAddresses(deposit.owner, account)
      )

    // Extract just the token IDs for positions owned by the user
    const userTokenIds = depositInfos.map((deposit) => deposit.tokenId)

    return { depositInfos, userTokenIds }
  }, [depositResults, tokenIds, account])

  // Fetch position details for each of the user's token IDs
  const positionInputs = useMemo(
    () => userDeposits.userTokenIds.map((tokenId) => [tokenId]),
    [userDeposits.userTokenIds]
  )

  const positionResults = useSingleContractMultipleData(positionManager, 'positions', positionInputs)
  const positionsLoading = useMemo(() => positionResults.some(({ loading }) => loading), [positionResults])
  const positionsError = useMemo(() => positionResults.some(({ error }) => error), [positionResults])

  // Process the position details
  const positions = useMemo(() => {
    if (!positionsLoading && !positionsError && userDeposits.userTokenIds.length > 0) {
      return positionResults
        .map((call, i) => {
          if (!call.result) return null
          const tokenId = userDeposits.userTokenIds[i]
          const result = call.result as CallStateResult
          const depositInfo = userDeposits.depositInfos.find(
            (deposit) => deposit.tokenId.toString() === tokenId.toString()
          )

          if (!depositInfo || !areEqualAddresses(depositInfo.owner, account)) {
            return null
          }

          return {
            tokenId,
            fee: result.fee,
            feeGrowthInside0LastX128: result.feeGrowthInside0LastX128,
            feeGrowthInside1LastX128: result.feeGrowthInside1LastX128,
            liquidity: result.liquidity,
            nonce: result.nonce,
            operator: result.operator,
            tickLower: result.tickLower,
            tickUpper: result.tickUpper,
            token0: result.token0,
            token1: result.token1,
            tokensOwed0: result.tokensOwed0,
            tokensOwed1: result.tokensOwed1,
            stakingInfo: {
              owner: depositInfo.owner,
              numberOfStakes: depositInfo.numberOfStakes,
            },
          }
        })
        .filter((position): position is StakedPositionDetails => position !== null)
    }
    return undefined
  }, [positionsLoading, positionsError, positionResults, userDeposits, account])

  return {
    loading: someTokenIdsLoading || balanceLoading || positionsLoading || depositsLoading,
    positions,
  }
}
